// src/contexts/SettingsContext.js

import React, {
	createContext,
	useContext,
	useReducer,
	useEffect,
	useCallback,
	useRef,
  } from "react";
  
  // --- Constants ---
  const LOCAL_STORAGE_KEY = "appUserSettings_v1";
  const AUDIO_API_URL = "https://samblouir.com/audio_route";
  
  // Available Voices (keep as is)
  export const AVAILABLE_VOICES = [ /* ... */
	  { name: "Aoede (Female)", value: "en-US-Standard-F" }, { name: "Puck (Male)", value: "en-US-Standard-B" },
	  { name: "Charon (Male)", value: "en-US-Wavenet-D" }, { name: "Kore (Female)", value: "en-US-Wavenet-C" },
	  { name: "Fenrir (Male)", value: "en-GB-Standard-D" }, { name: "Leda (Female)", value: "en-GB-Standard-A" },
	  { name: "Orus (Male)", value: "en-AU-Standard-B" }, { name: "Zephyr (Female)", value: "en-AU-Standard-A" },
  ];
  const DEFAULT_VOICE = "en-GB-Standard-A";
  const DEFAULT_SPEAKING_RATE = 1.0;
  const DEFAULT_PITCH = 0.0;
  
  // *** ADDED: Available Languages ***
  // Using lowercase codes for easier backend handling potentially
  export const AVAILABLE_LANGUAGES = [
	  { code: 'bn', name: 'Bengali' }, { code: 'zh-yue', name: 'Cantonese' }, // Use appropriate codes
	  { code: 'en', name: 'English' }, { code: 'fr', name: 'French' },
	  { code: 'de', name: 'German' }, { code: 'hi', name: 'Hindi' },
	  { code: 'zh-cmn', name: 'Mandarin' }, { code: 'fa', name: 'Persian' },
	  { code: 'pa', name: 'Punjabi' }, { code: 'es', name: 'Spanish' },
	  { code: 'tr', name: 'Turkish' }
  ];
  const DEFAULT_LANGUAGE_CODE = 'en'; // Default language code
  
  // TTS Limits (keep as is)
  const MIN_RATE = 0.25; const MAX_RATE = 4.0; const MIN_PITCH = -20.0; const MAX_PITCH = 20.0;
  
  // --- Define Action Types ---
  const ACTIONS = {
	SET_VOICE: "SET_VOICE",
	LOAD_SETTINGS: "LOAD_SETTINGS",
	TOGGLE_SETTINGS_PANEL: "TOGGLE_SETTINGS_PANEL",
	SET_SPEAKING_RATE: "SET_SPEAKING_RATE",
	SET_PITCH: "SET_PITCH",
	FETCH_SAMPLE_AUDIO_START: "FETCH_SAMPLE_AUDIO_START",
	FETCH_SAMPLE_AUDIO_SUCCESS: "FETCH_SAMPLE_AUDIO_SUCCESS",
	FETCH_SAMPLE_AUDIO_ERROR: "FETCH_SAMPLE_AUDIO_ERROR",
	CLEAR_SAMPLE_AUDIO: "CLEAR_SAMPLE_AUDIO",
	// *** ADDED: Action for language ***
	SET_LANGUAGE: "SET_LANGUAGE",
  };
  
  // --- Initial State Structure ---
  const initialSettingsState = {
	voice: DEFAULT_VOICE,
	speakingRate: DEFAULT_SPEAKING_RATE,
	pitch: DEFAULT_PITCH,
	language: DEFAULT_LANGUAGE_CODE, // *** ADDED language state ***
	// Transient state
	isSettingsPanelVisible: false,
	isSampleAudioLoading: false,
	sampleAudioError: null,
	sampleAudioUrl: null,
  };
  
  // --- Validation Helpers ---
  const getValidVoice = (v) => { /* ... */ const i = AVAILABLE_VOICES.some(opt => opt.value === v); return i ? v : DEFAULT_VOICE; };
  const getValidRate = (r) => { /* ... */ try { const n=parseFloat(r); if(isNaN(n))return DEFAULT_SPEAKING_RATE; return Math.max(MIN_RATE,Math.min(MAX_RATE,n)); } catch { return DEFAULT_SPEAKING_RATE; }};
  const getValidPitch = (p) => { /* ... */ try { const n=parseFloat(p); if(isNaN(p))return DEFAULT_PITCH; return Math.max(MIN_PITCH,Math.min(MAX_PITCH,p)); } catch { return DEFAULT_PITCH; }};
  // *** ADDED: Language validation ***
  const getValidLanguageCode = (langCode) => {
	  const isValid = AVAILABLE_LANGUAGES.some(lang => lang.code === langCode);
	  return isValid ? langCode : DEFAULT_LANGUAGE_CODE;
  };
  function cleanAssistantResponseForTTS(rawText) { /* ... keep as is ... */ if(!rawText)return"";let p=rawText.replace(/<[^>]*>/g,"").replace(/[\*_`]/g,'').replace(/&#39;/g,"'").replace(/&quot;/g,'"').replace(/&apos;/g,"'").replace(/&lt;/g,"<").replace(/&gt;/g,">").replace(/&amp;/g,"&");return p.trim() }
  
  
  // --- Reducer Function ---
  function settingsReducer(state, action) {
	switch (action.type) {
	  case ACTIONS.LOAD_SETTINGS:
		const loadedData = action.payload || {};
		return {
		  ...initialSettingsState, // Start fresh with code defaults for transient state
		  ...loadedData,          // Load saved settings
		  // Re-validate potentially saved settings
		  voice: getValidVoice(loadedData.voice),
		  speakingRate: getValidRate(loadedData.speakingRate),
		  pitch: getValidPitch(loadedData.pitch),
		  language: getValidLanguageCode(loadedData.language), // *** ADDED validation ***
		};
  
	  case ACTIONS.SET_VOICE: // ... keep ...
		  const v = getValidVoice(action.payload); return v === state.voice ? state : { ...state, voice: v };
	  case ACTIONS.TOGGLE_SETTINGS_PANEL: // ... keep ...
		  return { ...state, isSettingsPanelVisible: !state.isSettingsPanelVisible };
	  case ACTIONS.SET_SPEAKING_RATE: // ... keep ...
		  const r = getValidRate(action.payload); return r === state.speakingRate ? state : { ...state, speakingRate: r };
	  case ACTIONS.SET_PITCH: // ... keep ...
		  const p = getValidPitch(action.payload); return p === state.pitch ? state : { ...state, pitch: p };
  
	  // *** ADDED: Reducer case for language ***
	  case ACTIONS.SET_LANGUAGE:
		  const newLangCode = getValidLanguageCode(action.payload);
		  if (newLangCode === state.language) return state;
		  return { ...state, language: newLangCode };
  
	  // Sample audio cases remain unchanged
	  case ACTIONS.FETCH_SAMPLE_AUDIO_START: // ... keep ...
		  if (state.sampleAudioUrl) { URL.revokeObjectURL(state.sampleAudioUrl); }
		  return { ...state, isSampleAudioLoading: true, sampleAudioError: null, sampleAudioUrl: null };
	  case ACTIONS.FETCH_SAMPLE_AUDIO_SUCCESS: // ... keep ...
		  return { ...state, isSampleAudioLoading: false, sampleAudioUrl: action.payload, sampleAudioError: null };
	  case ACTIONS.FETCH_SAMPLE_AUDIO_ERROR: // ... keep ...
		  return { ...state, isSampleAudioLoading: false, sampleAudioError: action.payload, sampleAudioUrl: null };
	  case ACTIONS.CLEAR_SAMPLE_AUDIO: // ... keep ...
		   if (state.sampleAudioUrl) { URL.revokeObjectURL(state.sampleAudioUrl); }
		  return { ...state, sampleAudioUrl: null, sampleAudioError: null, isSampleAudioLoading: false };
  
	  default:
		return state;
	}
  }
  
  // --- Create Context ---
  const SettingsContext = createContext();
  
  // --- Settings Provider Component ---
  export function SettingsProvider({ children }) {
	const [settingsState, dispatchSettings] = useReducer(settingsReducer, initialSettingsState);
	const sampleAudioRef = useRef(null);
  
	// Effect to load saved settings
	useEffect(() => { /* ... keep as is ... */
		 try{const s=localStorage.getItem(LOCAL_STORAGE_KEY);const d=s?JSON.parse(s):null;dispatchSettings({type:ACTIONS.LOAD_SETTINGS,payload:d})}catch(e){console.error("Load err",e);dispatchSettings({type:ACTIONS.LOAD_SETTINGS,payload:null})}
	}, []);
	// Effect to save relevant settings
	useEffect(() => {
	  // Exclude transient state before saving
	  const { isSettingsPanelVisible, isSampleAudioLoading, sampleAudioError, sampleAudioUrl, ...settingsToSave } = settingsState;
  
	  // Prepare initial defaults for comparison (excluding transient)
	  const initialDefaultsToSave = { ...initialSettingsState };
	  delete initialDefaultsToSave.isSettingsPanelVisible;
	  delete initialDefaultsToSave.isSampleAudioLoading;
	  delete initialDefaultsToSave.sampleAudioError;
	  delete initialDefaultsToSave.sampleAudioUrl;
  
	  if (JSON.stringify(settingsToSave) === JSON.stringify(initialDefaultsToSave) && !localStorage.getItem(LOCAL_STORAGE_KEY)) { return; }
	  try {
		localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(settingsToSave));
	  } catch (error) { console.error("SettingsProvider: Error saving settings:", error); }
	// *** ADDED language to dependency array for saving ***
	}, [settingsState.voice, settingsState.speakingRate, settingsState.pitch, settingsState.language]);
  
  
	// --- Convenience Setters ---
	const setVoice = useCallback((v) => dispatchSettings({ type: ACTIONS.SET_VOICE, payload: v }), []);
	const toggleSettingsPanel = useCallback(() => dispatchSettings({ type: ACTIONS.TOGGLE_SETTINGS_PANEL }), []);
	const setSpeakingRate = useCallback((r) => dispatchSettings({ type: ACTIONS.SET_SPEAKING_RATE, payload: r }), []);
	const setPitch = useCallback((p) => dispatchSettings({ type: ACTIONS.SET_PITCH, payload: p }), []);
	// *** ADDED: Setter for language ***
	const setLanguage = useCallback((langCode) => dispatchSettings({ type: ACTIONS.SET_LANGUAGE, payload: langCode }), []);
	const clearSampleAudio = useCallback(() => dispatchSettings({ type: ACTIONS.CLEAR_SAMPLE_AUDIO }), []);
  
	// Async function to play sample audio (keep as is - uses current settings state)
	const playSampleAudio = useCallback(async () => { /* ... keep as is ... */
		  const textToPlay="Hello and welcome!";if(settingsState.isSampleAudioLoading){return}const settingsForApi={voice:settingsState.voice,speakingRate:settingsState.speakingRate,pitch:settingsState.pitch};dispatchSettings({type:ACTIONS.FETCH_SAMPLE_AUDIO_START});try{const fd=new FormData();fd.append("user_input",cleanAssistantResponseForTTS(textToPlay));fd.append("settings",JSON.stringify(settingsForApi));const r=await fetch(AUDIO_API_URL,{method:"POST",body:fd});if(!r.ok){let e="Err";try{e=await r.json()}catch(_){}throw new Error(`Aud ${r.status} ${e.error||""}`)}const blob=await r.blob();const url=URL.createObjectURL(blob);dispatchSettings({type:ACTIONS.FETCH_SAMPLE_AUDIO_SUCCESS,payload:url})}catch(err){console.error("Play Sample Err",err);dispatchSettings({type:ACTIONS.FETCH_SAMPLE_AUDIO_ERROR,payload:err.message||"Err"})}
	}, [settingsState.voice, settingsState.speakingRate, settingsState.pitch, settingsState.isSampleAudioLoading]); // Depend on settings used
  
  
	// Effect to automatically play sample audio (keep as is)
	useEffect(() => { /* ... keep as is ... */
		  if(settingsState.sampleAudioUrl&&sampleAudioRef.current){const a=sampleAudioRef.current;a.src=settingsState.sampleAudioUrl;a.play().catch(e=>{console.error("Sample Play Eff Err",e);dispatchSettings({type:ACTIONS.FETCH_SAMPLE_AUDIO_ERROR,payload:`Play err: ${e.message}`});URL.revokeObjectURL(settingsState.sampleAudioUrl)})}else if(!settingsState.sampleAudioUrl&&sampleAudioRef.current){const a=sampleAudioRef.current;if(!a.paused)a.pause();if(a.src&&a.src.startsWith('blob:')){}a.removeAttribute('src');a.load()}
	}, [settingsState.sampleAudioUrl]);
  
  
	// --- Context Value ---
	const contextValue = {
	  settingsState,
	  dispatchSettings,
	  setVoice, setSpeakingRate, setPitch, // Voice settings
	  setLanguage, // *** ADDED language setter ***
	  availableVoices: AVAILABLE_VOICES,
	  availableLanguages: AVAILABLE_LANGUAGES, // *** ADDED languages list ***
	  toggleSettingsPanel, // Panel visibility
	  playSampleAudio, clearSampleAudio, sampleAudioRef, // Sample audio
	};
  
	return (
	  <SettingsContext.Provider value={contextValue}>
		{children}
	  </SettingsContext.Provider>
	);
  }
  
  // --- Custom Hook for Consuming Settings ---
  export function useSettings() { /* ... keep as is ... */
	const context = useContext(SettingsContext);
	if (context === undefined) { throw new Error("useSettings must be used within a SettingsProvider"); }
	return context;
  }